"use client";

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import type { MotionValue } from "framer-motion";
import { useEffect } from "react";

import { cn } from "@/lib/utils";

/** Figma 8301:73281 — 80 ticks, left-to-right fill (same model as /our-customers). */
export const FINANCING_VIDEO_PROGRESS_BAR_COUNT = 80;

const SCRUBBER_SPRING = { stiffness: 100, damping: 26, mass: 0.45 } as const;

interface FinancingVideoProgressBarProps {
  /** Active episode index (0-based). */
  activeIndex: number;
  episodeCount: number;
  onSelectIndex?: (index: number) => void;
  className?: string;
}

export function FinancingVideoProgressBar({
  activeIndex,
  episodeCount,
  onSelectIndex,
  className,
}: FinancingVideoProgressBarProps) {
  const initialFill = episodeCount > 0 ? (activeIndex + 1) / episodeCount : 0;
  const progressTarget = useMotionValue(initialFill);
  const progress = useSpring(progressTarget, SCRUBBER_SPRING);

  useEffect(() => {
    const next =
      episodeCount > 0 ? Math.min(1, (activeIndex + 1) / episodeCount) : 0;
    progressTarget.set(next);
  }, [activeIndex, episodeCount, progressTarget]);

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 overflow-x-clip md:gap-[11px]",
        className
      )}
      role="tablist"
      aria-label="Customer story videos"
    >
      <div className="relative h-8 w-full min-w-0 overflow-x-clip">
        <ProgressTickRow className="absolute inset-0" played={false} />
        <AnimatedFill progress={progress}>
          <ProgressTickRow className="absolute inset-0" played />
        </AnimatedFill>
      </div>

      {onSelectIndex ? (
        <span className="sr-only">
          {Array.from({ length: episodeCount }, (_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              onClick={() => onSelectIndex(index)}
            >
              Video {index + 1}
            </button>
          ))}
        </span>
      ) : null}
    </div>
  );
}

function AnimatedFill({
  progress,
  children,
}: {
  progress: MotionValue<number>;
  children: React.ReactNode;
}) {
  const clipPath = useTransform(
    progress,
    (value) => `inset(0 ${(1 - Math.min(1, Math.max(0, value))) * 100}% 0 0)`
  );

  return (
    <motion.div
      className="absolute inset-0"
      style={{ clipPath }}
      data-testid="financing-video-progress-fill"
    >
      {children}
    </motion.div>
  );
}

function ProgressTickRow({
  className,
  played,
}: {
  className?: string;
  played: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-px overflow-x-clip md:justify-between md:gap-[11px]",
        className
      )}
      aria-hidden={!played}
    >
      {Array.from(
        { length: FINANCING_VIDEO_PROGRESS_BAR_COUNT },
        (_, index) => (
          <span
            key={index}
            className={cn(
              "h-8 min-w-0 flex-1 rounded-full md:w-[3px] md:min-w-[3px] md:flex-none md:shrink-0",
              played ? "bg-grey-950" : "bg-grey-100"
            )}
          />
        )
      )}
    </div>
  );
}
